import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';

interface FilterSummaryItem {
  label: string;
  count: number;
}

interface SaveFilterModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void> | void;
  summary?: FilterSummaryItem[];
  existingNames?: string[];
}

const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  open,
  onClose,
  onSave,
  summary = [],
  existingNames = [],
}) => {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setIsSaving(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const trimmedName = name.trim();
  const isDuplicate = existingNames.some(
    (existing) => existing.toLowerCase() === trimmedName.toLowerCase(),
  );
  const isReadyToSave = trimmedName.length > 0 && !isDuplicate && !isSaving;

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleSave = async () => {
    if (!isReadyToSave) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmedName);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className='modal-overlay'
      onClick={handleClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        className='modal-cont'
        style={{
          width: '480px',
          maxWidth: '90vw',
          padding: 0,
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='modal-header-cont-temp'
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--border-color)',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <div className='modal-header-cont-text'>
              <h1
                className='modal-header-cont-text-h1'
                style={{ fontSize: '20px', marginBottom: '4px' }}
              >
                Save Filter
              </h1>
              <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
                Save the current screening parameters to reuse them later.
              </p>
            </div>
            <div
              className='module-header-btn-close'
              onClick={handleClose}
              style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className='modal-body'
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <label
              style={{
                color: 'var(--text-primary-color)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Filter Name
            </label>
            <input
              type='text'
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                }
              }}
              placeholder='e.g. OFAC High Risk'
              style={{
                background: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: `1px solid ${
                  isDuplicate ? '#f04438' : 'var(--border-color)'
                }`,
                padding: '12px 14px',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            {isDuplicate && (
              <span style={{ color: '#f04438', fontSize: '13px' }}>
                A filter with this name already exists.
              </span>
            )}
          </div>

          {summary.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                width: '100%',
              }}
            >
              {summary.map((item) => (
                <span
                  key={item.label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  <BookmarkBorderOutlinedIcon
                    style={{ fontSize: 14, color: 'var(--action-color)' }}
                  />
                  {item.count} {item.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <button
            className='btn-outline'
            onClick={handleClose}
            disabled={isSaving}
            style={{ margin: 0, width: 'auto' }}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            onClick={handleSave}
            disabled={!isReadyToSave}
            style={{
              margin: 0,
              width: 'auto',
              opacity: isReadyToSave ? 1 : 0.5,
              cursor: isReadyToSave ? 'pointer' : 'not-allowed',
            }}
          >
            {isSaving ? 'Saving...' : 'Save Filter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveFilterModal;
